import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

// Perzistira filter/stranica state liste u URL query string umjesto u čisti
// useState — bez ovoga, odlazak na detalj (klik na redak) pa povratak (BackButton,
// koji interno zove navigate(-1)) vraća potpuno praznu listu jer se komponenta
// remounta i lokalni state gubi. setSearchParams(..., { replace: true }) znači da
// svaka izmjena filtera MIJENJA trenutni history entry (ne dodaje novi) — tako
// klik na "Natrag" s detalj-stranice i dalje ide točno jedan korak unatrag.
//
// Osnova za izmjenu je window.location.search, NE `prev` iz setSearchParams: react-router v6
// `prev` ne vidi izmjenu prethodnog poziva u istom ticku (remix-run/react-router#9757), pa bi
// više setera pozvanih zaredom (npr. "Primijeni" u MobileListFilters mijenja status + objekt
// odjednom) prebrisalo jedan drugoga — preživjela bi samo zadnja izmjena. replace navigacija
// sinkrono ažurira window.location, pa se uzastopni pozivi pravilno slažu.
const currentParams = () => new URLSearchParams(window.location.search);

/** String filter (npr. tražilica) — prazna/default vrijednost briše URL parametar. */
export const useUrlTextParam = (
  key: string,
  defaultValue = ''
): [string, (value: string) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(key) ?? defaultValue;

  const setValue = useCallback(
    (next: string) => {
      setSearchParams(
        () => {
          const params = currentParams();
          if (!next || next === defaultValue) params.delete(key);
          else params.set(key, next);
          return params;
        },
        { replace: true }
      );
    },
    [key, defaultValue, setSearchParams]
  );

  return [value, setValue];
};

/** Clearable FormSelect filter čija je "nema odabira" vrijednost null (ne prazan string). */
export const useUrlNullableParam = (
  key: string
): [string | null, (value: string | null) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(key);

  const setValue = useCallback(
    (next: string | null) => {
      setSearchParams(
        () => {
          const params = currentParams();
          if (!next) params.delete(key);
          else params.set(key, next);
          return params;
        },
        { replace: true }
      );
    },
    [key, setSearchParams]
  );

  return [value, setValue];
};

/** Broj stranice (1-indeksirano) — stranica 1 se ne upisuje u URL (čist izgled
 * kad lista nije paginirana dalje od prve stranice). */
export const useUrlPageParam = (key = 'page'): [number, (value: number) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = Number(searchParams.get(key));
  const value = Number.isInteger(raw) && raw > 1 ? raw : 1;

  const setValue = useCallback(
    (next: number) => {
      setSearchParams(
        () => {
          const params = currentParams();
          if (!next || next <= 1) params.delete(key);
          else params.set(key, String(next));
          return params;
        },
        { replace: true }
      );
    },
    [key, setSearchParams]
  );

  return [value, setValue];
};
